/**
 * Engagement action display configuration
 * Maps API action codes to UI presentation
 */

export const ENGAGEMENT_ACTIONS = {
  // Primary workflow actions
  SAVE: 'save',
  SUBMIT: 'submit',
  SUBMIT_FOR_REVIEW: 'submit-for-review',
  APPROVE: 'approve',
  DECLINE: 'decline',
  DISCARD: 'discard',
  
  // Secondary workflow actions
  RECALL: 'recall',
  RECALL_TO_ACCOUNTING: 'recall-to-accounting',
  COMPLETE: 'complete',
  REACTIVATE: 'reactivate',
  
  // Special actions
  CREATE_CHANGE_REQUEST: 'createChangeRequest',
  REFRESH: 'refresh',
  
  // Audit-only actions (not directly user-actionable)
  DISCARD_REVERT_BASE: 'discard-revert-base',
  CREATE_VERSION: 'create-version',
  APPROVE_REPLACE_BASE: 'approve-replace-base',
  TERMINATE_WORKER: 'terminate-worker',
  APPLY_SCHEDULED_BUDGETED_HOURS: 'apply-scheduled-budgeted-hours',
} as const

export type EngagementActionCode = typeof ENGAGEMENT_ACTIONS[keyof typeof ENGAGEMENT_ACTIONS]

/**
 * Action display metadata for UI rendering
 */
export interface ActionDisplayConfig {
  label: string
  loadingLabel?: string
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
}

/**
 * Convert kebab-case action code to camelCase dictionary key
 * @example 'submit-for-review' -> 'submitForReview'
 * @example 'save' -> 'save'
 */
function actionCodeToDictionaryKey(actionCode: string): string {
  return actionCode.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * Default action labels (English)
 * These can be overridden by i18n dictionary
 */
export const ACTION_LABELS: Record<EngagementActionCode, ActionDisplayConfig> = {
  [ENGAGEMENT_ACTIONS.SAVE]: {
    label: 'Save',
    loadingLabel: 'Saving...',
  },
  [ENGAGEMENT_ACTIONS.SUBMIT]: {
    label: 'Submit',
    loadingLabel: 'Submitting...',
  },
  [ENGAGEMENT_ACTIONS.SUBMIT_FOR_REVIEW]: {
    label: 'Submit for Review',
    loadingLabel: 'Submitting...',
  },
  [ENGAGEMENT_ACTIONS.APPROVE]: {
    label: 'Approve',
    loadingLabel: 'Approving...',
  },
  [ENGAGEMENT_ACTIONS.DECLINE]: {
    label: 'Decline',
    loadingLabel: 'Declining...',
    variant: 'destructive',
  },
  [ENGAGEMENT_ACTIONS.DISCARD]: {
    label: 'Discard',
    loadingLabel: 'Discarding...',
    variant: 'destructive',
  },
  [ENGAGEMENT_ACTIONS.RECALL]: {
    label: 'Recall',
    loadingLabel: 'Recalling...',
  },
  [ENGAGEMENT_ACTIONS.RECALL_TO_ACCOUNTING]: {
    label: 'Recall to Accounting',
    loadingLabel: 'Recalling...',
  },
  [ENGAGEMENT_ACTIONS.COMPLETE]: {
    label: 'Mark as Complete',
    loadingLabel: 'Completing...',
  },
  [ENGAGEMENT_ACTIONS.REACTIVATE]: {
    label: 'Move back to Active',
    loadingLabel: 'Reactivating...',
  },
  [ENGAGEMENT_ACTIONS.CREATE_CHANGE_REQUEST]: {
    label: 'Edit',
    loadingLabel: 'Creating...',
  },
  [ENGAGEMENT_ACTIONS.REFRESH]: {
    label: 'Cancel Changes',
  },
  // Audit-only actions (not directly user-actionable)
  [ENGAGEMENT_ACTIONS.DISCARD_REVERT_BASE]: {
    label: 'Cancel Edit',
  },
  [ENGAGEMENT_ACTIONS.CREATE_VERSION]: {
    label: 'Edit',
  },
  [ENGAGEMENT_ACTIONS.APPROVE_REPLACE_BASE]: {
    label: 'Replace',
  },
  [ENGAGEMENT_ACTIONS.TERMINATE_WORKER]: {
    label: 'Terminate Worker',
  },
  [ENGAGEMENT_ACTIONS.APPLY_SCHEDULED_BUDGETED_HOURS]: {
    label: 'Apply Scheduled Hours',
  },
}

/**
 * Get action display label with i18n dictionary support
 * Checks dictionary first, then falls back to ACTION_LABELS
 */
export function getActionLabel(
  actionCode: EngagementActionCode,
  dictionary?: Record<string, any>
): string {
  // Try dictionary lookup by action code (kebab-case to camelCase)
  const dictionaryKey = actionCodeToDictionaryKey(actionCode)
  const dictionaryLabel = dictionary?.engagement?.actions?.[dictionaryKey]
  
  return dictionaryLabel || ACTION_LABELS[actionCode]?.label || actionCode
}

/**
 * Get loading label for an action with i18n dictionary support
 * Checks dictionary first, then falls back to ACTION_LABELS
 */
export function getActionLoadingLabel(
  actionCode: EngagementActionCode,
  dictionary?: Record<string, any>
): string {
  // Try dictionary lookup for loading label (e.g., "savingLabel")
  const dictionaryKey = actionCodeToDictionaryKey(actionCode)
  const dictionaryLoadingLabel = dictionary?.engagement?.actions?.[`${dictionaryKey}Loading`]
  
  return dictionaryLoadingLabel || ACTION_LABELS[actionCode]?.loadingLabel || ACTION_LABELS[actionCode]?.label || actionCode
}

